/**
 * 검색 입력 필드 컴포넌트
 *
 * 검색 아이콘 + 입력 필드 + 클리어 버튼 패턴을 캡슐화한 컴포넌트입니다.
 * 디자인 시스템의 input-base 스타일을 활용합니다.
 *
 * @component
 * @example
 * <AppSearchInput
 *   value={searchQuery}
 *   onChange={setSearchQuery}
 *   placeholder="플레이리스트 검색"
 * />
 */

import { useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AppSearchInputProps {
  /** 검색어 */
  value: string;
  /** 검색어 변경 핸들러 */
  onChange: (value: string) => void;
  /** 플레이스홀더 텍스트 */
  placeholder?: string;
  /** 추가 CSS 클래스 (외부 컨테이너에 적용) */
  className?: string;
  /** 테스트 ID */
  'data-testid'?: string;
}

export function AppSearchInput({
  value,
  onChange,
  placeholder = '검색...',
  className,
  'data-testid': dataTestId,
}: AppSearchInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    [onChange]
  );

  const handleClear = useCallback(() => onChange(''), [onChange]);

  return (
    <div className={cn('relative flex-1', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        className="input-base pl-10 pr-10"
        data-testid={dataTestId}
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
          aria-label="검색어 지우기"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
